import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import { useLingui } from "@lingui/react/macro";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /**
   * Text only: a labelled field here would clip under the title, because this
   * dialog removes the top padding the theme keeps for a first field's shrunk
   * label (TASK-BUGFIX-MUI-FIELD-LABEL-NOTCH). A form belongs in FormDialog.
   */
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  destructive?: boolean;
  /**
   * Optional THIRD action, rendered last and styled as the primary. Supplying it
   * turns this into a three-outcome dialog (cancel / confirm / tertiary) — the
   * shape SPEC-FRONTEND-RULE-065 requires for an unsaved-draft guard, where the
   * destructive "discard" must not be the primary action (RULE-044).
   *
   * When BOTH `tertiaryText` and `onTertiary` are absent the component renders
   * exactly as it always has — same two buttons, same order, same styling, same
   * focus behaviour. 24 files import this component; that non-regression is
   * asserted by test, not assumed.
   */
  tertiaryText?: string;
  /** Handler for the optional third action. See {@link ConfirmDialogProps.tertiaryText}. */
  onTertiary?: () => void;
  /**
   * Which action receives initial focus. Omitted (the default) means no button
   * is auto-focused — today's behaviour for every existing consumer.
   *
   * Pass `"cancel"` when the dialog guards data loss: the safe choice must be the
   * path of least resistance, and the destructive one must never be default-focused
   * (SPEC-FRONTEND-RULE-044; UX Rulebook 6.5).
   */
  initialFocus?: "cancel" | "confirm";
}

/**
 * Metrics shared by the OPTIONAL third action and the demoted destructive action.
 * Declared once so the new code adds no further magic values, and matched to the
 * dialog's existing action buttons so the three read as one row (RULE-034).
 */
const ACTION_METRICS = {
  textTransform: "none",
  fontSize: "0.8125rem",
  fontWeight: 500,
  px: 2,
  py: 0.75,
  // Floor, not a preference: RULE-046 requires every target >= 24x24 CSS px, and
  // shrinking a three-button row is the easiest way to breach it by accident.
  minHeight: 32,
} as const;

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  destructive = false,
  tertiaryText,
  onTertiary,
  initialFocus,
}) => {
  const { t } = useLingui();
  const confirmLabel = confirmText ?? t`Confirm`;
  const cancelLabel = cancelText ?? t`Cancel`;
  // A third action is only rendered when BOTH halves are supplied — a label with
  // no handler would render a dead button, which is worse than no button.
  const hasTertiary = Boolean(tertiaryText && onTertiary);
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      // A three-outcome guard is a short question, so it gets a narrower shell.
      // Scoped to that variant: every existing two-button consumer keeps "sm".
      maxWidth={hasTertiary ? "xs" : "sm"}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          boxShadow:
            "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: hasTertiary ? "1rem" : "18px",
          color: colors.text.default,
          pb: 1,
          ...(hasTertiary && { px: 2.5, pt: 2.5 }),
        }}
      >
        {title}
      </DialogTitle>
      {/* No top padding after the title: the theme gives a title + content
          pair room for a shrunk field label (TASK-BUGFIX-MUI-FIELD-LABEL-NOTCH),
          but a confirm body is text only, so it keeps its tight look. The
          selector matches the theme's, and sx comes later, so it wins. */}
      <DialogContent
        sx={{
          ".MuiDialogTitle-root + &": { pt: 0 },
          ...(hasTertiary && { px: 2.5, pb: 1 }),
        }}
      >
        <DialogContentText
          sx={{
            color: colors.text.secondary,
            fontSize: hasTertiary ? "0.8125rem" : "14px",
            lineHeight: "1.5",
          }}
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          px: hasTertiary ? 2.5 : 3,
          pb: hasTertiary ? 2.5 : 3,
          gap: 1,
        }}
      >
        <Button
          onClick={onCancel}
          variant="outlined"
          autoFocus={initialFocus === "cancel"}
          sx={{
            ...(hasTertiary
              ? ACTION_METRICS
              : { textTransform: "none", fontSize: "14px", fontWeight: 500, px: 3, py: 1 }),
            borderColor: colors.border.default,
            color: colors.text.default,
            bgcolor: "transparent",
            "&:hover": {
              bgcolor: "#f9fafb",
              borderColor: colors.border.default,
            },
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          // With a tertiary action present, the tertiary is the primary and this
          // (destructive) action steps down to outlined — RULE-044: the destructive
          // control must not be the primary. Without one, styling is unchanged.
          variant={hasTertiary ? "outlined" : "contained"}
          autoFocus={initialFocus === "confirm"}
          sx={
            hasTertiary
              ? {
                  ...ACTION_METRICS,
                  borderColor: colors.status.error,
                  color: colors.status.error,
                  bgcolor: "transparent",
                  "&:hover": {
                    borderColor: colors.status.error,
                    bgcolor: alpha(colors.status.error, 0.08),
                  },
                }
              : {
                  textTransform: "none",
                  bgcolor: destructive ? colors.status.error : colors.text.clickable,
                  color: "white",
                  fontSize: "14px",
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: destructive ? colors.status.errorHover : "#2563eb",
                    boxShadow: "none",
                  },
                }
          }
        >
          {confirmLabel}
        </Button>
        {hasTertiary && (
          <Button
            onClick={onTertiary}
            variant="contained"
            sx={{
              ...ACTION_METRICS,
              // colors.button.* is the house PRIMARY-ACTION token (what the canonical
              // SaveButton and the Playground's own save control use). The previous
              // colors.text.clickable is a TEXT grey (#4B5563) and rendered this
              // button looking disabled rather than primary.
              bgcolor: colors.button.active,
              color: "white",
              boxShadow: "none",
              "&:hover": {
                bgcolor: colors.button.activeHover,
                boxShadow: "none",
              },
            }}
          >
            {tertiaryText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
